import type { FileType } from '@unifyapps/defs/blocks/FileUpload/types';
import type { BlockStateUnionType } from '@unifyapps/defs/types/block';
import { ApplicationType, ColorScheme } from '@unifyapps/defs/types/interface';
import isEqual from 'react-fast-compare';
import InterfaceHelper from '../../../stores/InterfaceStore/helper/InterfaceHelper';
import { getUserContextWithLegacyDeviceContext } from '../../../utils/deviceContextAdapters';
import {
  attachInstances,
  type RepeatableActions,
  type RuntimeSourceInfo,
} from '../../../utils/repeatables/attachInstances';
import {
  attachRowScopeIdentities,
  type RowScope,
} from '../../../utils/repeatables/attachRowScopeIdentities';
import { filterRuntimeSourceInfoByCollectChildStates } from '../../../utils/repeatables/filterRuntimeSourceInfoByCollectChildStates';
import { isRuntimeBlockId } from '../../../utils/runTimeBlocks';
import { ComputeContextKeys, getContextUtils, getMobileContextUtils } from '../const';
import { adaptPageVariablesForContext } from '../utils/adaptPageVariablesForContext';
import { getCombinedBlockMethodsInBlocksState } from '../utils/getCombinedBlockMethodsInBlocksState';
import { getCombinedBlockState } from '../utils/getCombinedBlockState';
import { getCombinedDataSourceState } from '../utils/getCombinedDataSourceState';
import { getCombinedPageFunctions } from '../utils/getCombinedPageFunctions';
import { getCombinedPageVariables } from '../utils/getCombinedPageVariables';
import getInterfaceRecordForState from '../utils/getInterfaceRecordForState';
import {
  isRuntimeSourceBlockIdsEqual,
  isRuntimeSourceInfoContentEqual,
  isShallowEqualRuntimeSourceInfo,
  sharedReadonlyBaseCache,
  sharedRowOverlayCache,
} from './sharedContextCache';
import type { ComputeContextType, ContextMethodsState, ContextState } from './types';

/**
 * ContextBuilder — turns the live no-code stores into the `context` object that every
 * block's bindings evaluate against (`{{ b_input.value }}`, visibility rules, formulas).
 * ONE builder is created per block, per render (via ContextFactory) — hundreds of builders
 * per store change on a big page — so most of this file is caching to keep that work O(N),
 * not O(N²). Read `build()` first, then walk the cache ladder below.
 *
 * ── What a builder reads ────────────────────────────────────────────────────────────
 *   ContextState        (types.ts)  store slices: blockStates, dataSources, pageVariables…
 *   ContextMethodsState (types.ts)  method bags: blockMethods, actions, … — EMPTY in
 *                                   readonly mode, non-empty only when a block can act
 *                                   (event handlers). This readonly-vs-mutable split is
 *                                   what every cache below hinges on.
 *   RuntimeSourceInfo   (attachInstances.ts)  the Repeatable wiring — picture it as:
 *
 *       repeatables:       { b_repeat: { data:[…], primaryKey, itemPrimaryKeys, itemKeyToIndex } }
 *       childToRepeatable: { b_card: 'b_repeat', b_title: 'b_repeat' }   // block → its Repeatable
 *
 *     i.e. "which Repeatables exist (and their row data), and which blocks live inside one."
 *     Each builder mints its OWN wrapper object for the same underlying data, so cross-builder
 *     comparisons go field-by-field (isRuntimeSourceInfoContentEqual), never by wrapper ref.
 *
 * ── The cache ladder (cheapest hit first) ──────────────────────────────────────────────
 *   build() → getOrBuildBaseContext():
 *     ① own base    — I built this exact base last render          (canReuseBaseContext)
 *     ② shared base — a sibling readonly block already built it     (sharedReadonlyBaseCache,
 *                     keyed on the blockStates ref)                 ← the O(N²)→O(N) win
 *     ③ build fresh — buildBaseContext()
 *   then, only for blocks INSIDE a Repeatable row (blocksRuntimeState present):
 *     ④ own result     — my last full result is still valid
 *     ⑤ shared overlay — a sibling in my row already built it       (sharedRowOverlayCache)
 *
 *   "Shareable" (isShareableReadonly) = the readonly shape = no per-block closures baked into
 *   the base, so one base is safe to hand to every sibling. Mutable builders (action handlers,
 *   repeatable mutations) never share — their base carries closures specific to that block.
 */
class ContextBuilder {
  private static readonly STATE_KEYS: readonly (keyof ContextState)[] = Object.keys({
    mediaUploads: true,
    blockStates: true,
    blockRefs: true,
    dataSources: true,
    dataSourcesMeta: true,
    pageInputs: true,
    pageParams: true,
    location: true,
    userContext: true,
    deviceContext: true,
    appsAndIntegrations: true,
    pageVariables: true,
    pageFunctions: true,
    sessionStorage: true,
    localStorage: true,
    walkthrough: true,
    envVariables: true,
    queryMeta: true,
    colorScheme: true,
    interfaceRecord: true,
    activeAppVariantKey: true,
    activeTheme: true,
    availableThemes: true,
  } satisfies Record<keyof ContextState, true>) as (keyof ContextState)[];

  // Same contract as STATE_KEYS for the method bags; exhaustiveness compile-checked.
  private static readonly METHODS_KEYS: readonly (keyof ContextMethodsState)[] = Object.keys({
    blockMethods: true,
    dataSourceMethods: true,
    pageVariableMethods: true,
    pageFunctionMethods: true,
    localStorageMethods: true,
    sessionStorageMethods: true,
    actions: true,
  } satisfies Record<keyof ContextMethodsState, true>) as (keyof ContextMethodsState)[];

  private state: ContextState;
  private methods: ContextMethodsState;

  // Instance-scoped cache (NOT static): multi-page-instance pages (copilot threads,
  // embedded apps) keep multiple builders alive; a shared static cache let one
  // builder's stale entries answer another's queries (cross-context bleed).
  private lastBuildInputs: ContextState | undefined;
  private lastBuildMethods: ContextMethodsState | undefined;
  private lastBaseContext: ComputeContextType | undefined;

  // cache for final result (with blocksRuntimeState merged)
  private lastBuildResult: ComputeContextType | undefined;
  private lastBuildBlocksRuntimeState: Record<string, Record<string, unknown>> | undefined;
  private lastBuildRuntimeSourceInfo: RuntimeSourceInfo | undefined;
  private lastBuildRepeatableActions: RepeatableActions | undefined;
  private lastBuildRuntimeSourceBlockIds: ReadonlySet<string> | undefined;

  // 2000 entries at prod scale; both buildBaseContext and applyRuntimeBlocksContext
  // call this within the same build() with identical refs, so the second is a `===` hit.
  private lastCombinedBlocksStateBlocks:
    | Record<string, BlockStateUnionType | undefined>
    | undefined;
  private lastCombinedBlocksStateMethods: Record<string, unknown> | undefined;
  private lastCombinedBlocksStateResult:
    | (Record<string, BlockStateUnionType | undefined> & {
        runtimeBlocks?: Record<string, BlockStateUnionType | undefined>;
      })
    | undefined;

  private blocksRuntimeState: Record<string, Record<string, unknown>> | undefined;

  private runtimeSourceInfo: RuntimeSourceInfo | undefined;

  // Inner-first stack of {parentBlockId, instanceKey} entries. attachRowScopeIdentities
  // rewrites top-level sibling entries to the matching ancestor row's runtime instance
  // for implicit "this row" scoping with outer-row reach via stack walk.
  private rowScopeStack: readonly RowScope[] | undefined;

  // Store actions for repeatable aggregate methods (resetInstances, callBlockMethod).
  // When absent, mutating methods aren't attached but the read-only `instances`
  // aggregate still is.
  private repeatableActions: RepeatableActions | undefined;

  // Block IDs that are themselves runtime-source blocks (Repeatable, TreeView,
  // dynamic Carousel/SwipeableStack); used to exclude nested repeatables from a
  // parent's flat row aggregate (their data isn't scalar).
  private runtimeSourceBlockIds: ReadonlySet<string> | undefined;

  constructor() {
    this.state = {
      mediaUploads: {},
      blockStates: {},
      blockRefs: {},
      dataSources: {},
      dataSourcesMeta: {},
      pageInputs: {},
      pageParams: {},
      location: {},
      userContext: {},
      deviceContext: {},
      appsAndIntegrations: {},
      pageVariables: {},
      pageFunctions: {},
      sessionStorage: {},
      localStorage: {},
      walkthrough: {},
      envVariables: {},
      queryMeta: {},
      colorScheme: ColorScheme.LIGHT,
      interfaceRecord: undefined,
      activeAppVariantKey: undefined,
      activeTheme: undefined,
      availableThemes: undefined,
    };

    this.methods = {
      blockMethods: {},
      dataSourceMethods: {},
      pageVariableMethods: {},
      pageFunctionMethods: {},
      localStorageMethods: {},
      sessionStorageMethods: {},
      actions: {},
    };

    this.blocksRuntimeState = undefined;
    this.runtimeSourceInfo = undefined;
    this.rowScopeStack = undefined;
    this.repeatableActions = undefined;
    this.runtimeSourceBlockIds = undefined;
  }

  /**
   * Updates state with new values, maintaining immutability
   */
  private setState(newState: Partial<ContextState>): this {
    this.state = { ...this.state, ...newState };
    return this;
  }

  private hashKeys(keys: string[]): string {
    return keys.sort().join(',');
  }

  /**
   * Checks if this builder's own last-built base context can be reused.
   */
  private canReuseBaseContext(): boolean {
    if (!this.lastBuildInputs || !this.lastBaseContext || !this.lastBuildMethods) return false;
    return this.stateMatchesSnapshot(
      this.lastBuildInputs,
      this.lastBuildMethods,
      this.lastBuildRuntimeSourceInfo,
      this.lastBuildRepeatableActions,
      this.lastBuildRuntimeSourceBlockIds,
    );
  }

  /**
   * Would a base context built from `snapshot` be identical to one built from the
   * current state/methods? Same ref-check-then-deep-compare strategy for both the
   * per-instance cache and the process-wide readonly base cache.
   */
  // optimize for perf.mdc: minimize iteration, avoid object shape churn, hoist loop invariants
  private stateMatchesSnapshot(
    inputs: ContextState,
    methods: ContextMethodsState,
    runtimeSourceInfo: RuntimeSourceInfo | undefined,
    repeatableActions: RepeatableActions | undefined,
    runtimeSourceBlockIds: ReadonlySet<string> | undefined,
    // When comparing against a snapshot built by a DIFFERENT builder (the shared
    // readonly cache), RepeatableInfo wrappers differ by ref, so compare their
    // fields instead. Own-cache comparisons keep the faster wrapper-ref check.
    crossBuilder = false,
  ): boolean {
    for (const typedKey of ContextBuilder.STATE_KEYS) {
      const current = this.state[typedKey];
      const previous = inputs[typedKey];
      // identical reference → unchanged, no compare needed
      if (current === previous) continue;
      // blockStates/blockRefs are large maps updated immutably by their stores, so a
      // changed reference is always a real content change. Skip the O(n) deep compare —
      // it would walk ~every block just to confirm "changed". This is the hot path when
      // a large repeatable mounts and blockStates churns on every notification.
      if (typedKey === 'blockStates' || typedKey === 'blockRefs') return false;
      // Smaller slices can get a fresh reference with identical content (rebuilt per
      // render), so deep-compare them to avoid needless base-context rebuilds.
      if (!isEqual(current, previous)) return false;
    }

    // `instances` proxies are baked into the cached base context. If the runtime
    // source map changed (e.g., a repeatable's data array was reassigned), the
    // cached proxies are stale — invalidate. Shallow per-entry === walk is sound
    // here because every field is a ref the producer treats as immutable (data
    // is the parent's evaluatedData; itemPrimaryKeys/itemKeyToIndex are rebuilt
    // when content changes; primaryKey is the parent's content.primaryKey ref).
    const runtimeSourceInfoEqual = crossBuilder
      ? isRuntimeSourceInfoContentEqual(this.runtimeSourceInfo, runtimeSourceInfo)
      : isShallowEqualRuntimeSourceInfo(this.runtimeSourceInfo, runtimeSourceInfo);
    if (!runtimeSourceInfoEqual) {
      return false;
    }

    // resetInstances / callBlockMethod are baked-in closures over the current
    // repeatableActions reference. Different actions identity → stale closures,
    // must invalidate.
    if (this.repeatableActions !== repeatableActions) {
      return false;
    }

    // runtimeSourceBlockIds decides which children attachInstances bakes into the
    // base as nested-repeatable vs scalar row values. Siblings share one page-level
    // ref (cheap `===`), so this only walks on a genuine content difference; a base
    // built under one set must never be adopted by a builder whose set differs.
    if (!isRuntimeSourceBlockIdsEqual(this.runtimeSourceBlockIds, runtimeSourceBlockIds)) {
      return false;
    }

    // if the length of methods changes from 0 to non-zero, then we cannot reuse the base context
    // for each methods
    for (const methodKey of ContextBuilder.METHODS_KEYS) {
      const previousMethodKeys = Object.keys(methods[methodKey]);
      const currentMethodKeys = Object.keys(this.methods[methodKey]);
      // if current methods length is 0, then we can reuse the base context because
      // even if we have the methods from the previous build, as mutable context can be used in readonly mode
      // but not the other way around
      if (currentMethodKeys.length === 0) {
        continue;
      }
      if (this.hashKeys(currentMethodKeys) !== this.hashKeys(previousMethodKeys)) {
        return false;
      }
    }

    return true;
  }

  // Single dispatch funnel: row-scope identity rewrites and the `instances`
  // accessor both route through the same callBlockMethod action.
  private getCallMethodForRowScope():
    | ((params: {
        blockId: string;
        targetBlockId: string;
        methodName: string;
        methodPayload: unknown;
      }) => void)
    | undefined {
    const repeatableActions = this.repeatableActions;
    if (!repeatableActions) return undefined;
    return (params) => {
      void repeatableActions.callBlockMethod(params);
    };
  }

  private getCachedCombinedBlocksWithMethods(
    blockStates: Record<string, BlockStateUnionType | undefined>,
    blockMethods: Record<string, unknown>,
  ) {
    if (
      this.lastCombinedBlocksStateBlocks === blockStates &&
      this.lastCombinedBlocksStateMethods === blockMethods &&
      this.lastCombinedBlocksStateResult !== undefined
    ) {
      return this.lastCombinedBlocksStateResult;
    }
    const result = getCombinedBlockMethodsInBlocksState({
      blocksState: blockStates,
      blocksMethods: blockMethods,
    });
    this.lastCombinedBlocksStateBlocks = blockStates;
    this.lastCombinedBlocksStateMethods = blockMethods;
    this.lastCombinedBlocksStateResult = result;
    return result;
  }

  // Outside-Repeatable enrichment: attach `.instances` proxies so cross-row
  // bindings (e.g. `b_rep.instances[0].value` from a card outside any Repeatable)
  // resolve. Reads instances from raw blockStates because after
  // getCombinedBlockMethodsInBlocksState runtime IDs are nested under
  // `.runtimeBlocks`; the helper threads `instanceLookupState: blockStates` through.
  //
  // Gated on `collectChildStates`: only Repeatables whose bindings need the
  // cross-row `.instances[N]` accessor pay the proxy synthesis cost. When no
  // parent opts in, the filter returns `undefined` and we skip attachInstances
  // entirely, returning the input ref so downstream identity checks stay stable.
  private attachInstancesToBlocksState(
    blocksStateWithMethod: Record<string, BlockStateUnionType | undefined> & {
      runtimeBlocks?: Record<string, BlockStateUnionType | undefined>;
    },
  ): Record<string, BlockStateUnionType | undefined> & {
    runtimeBlocks?: Record<string, BlockStateUnionType | undefined>;
  } {
    if (
      !this.runtimeSourceInfo ||
      Object.keys(this.runtimeSourceInfo.childToRepeatable).length === 0
    ) {
      return blocksStateWithMethod;
    }

    const instancesRuntimeSourceInfo = filterRuntimeSourceInfoByCollectChildStates(
      this.runtimeSourceInfo,
      this.state.blockStates,
    );
    if (!instancesRuntimeSourceInfo) {
      return blocksStateWithMethod;
    }

    return attachInstances({
      blocksState: blocksStateWithMethod,
      runtimeSourceInfo: instancesRuntimeSourceInfo,
      instanceLookupState: this.state.blockStates,
      repeatableActions: this.repeatableActions,
      runtimeSourceBlockIds: this.runtimeSourceBlockIds,
      blockMethods: this.methods.blockMethods,
    });
  }

  // Inside-Repeatable enrichment: rewrite top-level sibling entries to the
  // current row's runtime instance so bare `b_outerInput.value` from inside an
  // inner row resolves via the inner-first stack walk in
  // attachRowScopeIdentities. Only meaningful when computing context FROM
  // inside a Repeatable row (rowScopeStack is non-empty); base context never
  // takes this path.
  private attachRowScopeToBlocksState(
    blocksState: Record<string, BlockStateUnionType | undefined> & {
      runtimeBlocks?: Record<string, BlockStateUnionType | undefined>;
    },
  ): Record<string, BlockStateUnionType | undefined> & {
    runtimeBlocks?: Record<string, BlockStateUnionType | undefined>;
  } {
    if (!this.runtimeSourceInfo || !this.rowScopeStack || this.rowScopeStack.length === 0) {
      return blocksState;
    }
    return attachRowScopeIdentities({
      blocksState,
      runtimeSourceInfo: this.runtimeSourceInfo,
      instanceLookupState: this.state.blockStates,
      rowScopeStack: this.rowScopeStack,
      blockMethods: this.methods.blockMethods,
      callMethod: this.getCallMethodForRowScope(),
    });
  }

  /**
   * Builds the base context without blocksRuntimeState
   * This is cached and reused for 99% of blocks
   */
  private buildBaseContext(): ComputeContextType {
    const { blockStates, dataSources, pageVariables, pageFunctions, interfaceRecord } = this.state;
    const { blockMethods, dataSourceMethods, pageVariableMethods, pageFunctionMethods } =
      this.methods;

    const blocksStateWithMethod = this.getCachedCombinedBlocksWithMethods(
      blockStates,
      blockMethods,
    );

    // Outside-Repeatable enrichment only: attach `.instances` proxies for
    // cross-row bindings rendered from outside any Repeatable. Row-scope
    // identity rewrites are deferred to applyRuntimeBlocksContext, which is
    // the only path that has a non-empty rowScopeStack in production.
    const enrichedBlocksState = this.attachInstancesToBlocksState(blocksStateWithMethod);

    // build combined block state without runtime state
    const combinedBlockState = getCombinedBlockState({
      blocksState: enrichedBlocksState,
      blocksRuntimeState: undefined,
    });

    const combinedDataSourceState = getCombinedDataSourceState({
      dataSourcesState: dataSources,
      dataSourceMethods: dataSourceMethods,
    });

    const adaptedPageVariables = adaptPageVariablesForContext(pageVariables);

    const combinedPageVariables = getCombinedPageVariables({
      pageVariables: adaptedPageVariables,
      pageVariablesMethods: pageVariableMethods,
    });

    const combinedPageFunctions = getCombinedPageFunctions({
      pageFunctions,
      pageFunctionMethods,
    });

    // separate runtime keys from regular keys in mediaUploads
    const mediaUploadsRegular: Record<string, FileType[] | undefined> = {};
    const mediaUploadsRuntime: Record<string, FileType[] | undefined> = {};
    const mediaUploadsState = this.state.mediaUploads ?? {};

    for (const blockId of Object.keys(mediaUploadsState)) {
      if (isRuntimeBlockId(blockId)) {
        mediaUploadsRuntime[blockId] = mediaUploadsState[blockId];
      } else {
        mediaUploadsRegular[blockId] = mediaUploadsState[blockId];
      }
    }

    const mediaUploads = {
      ...mediaUploadsRegular,
      ...(Object.keys(mediaUploadsRuntime).length > 0 && { runtimeBlocks: mediaUploadsRuntime }),
    };

    const result: ComputeContextType = {
      blockRefs: this.state.blockRefs,
      dataSourcesMeta: this.state.dataSourcesMeta,
      [ComputeContextKeys.WALKTHROUGH]: this.state.walkthrough,
      [ComputeContextKeys.RUNTIME_BLOCKS]: combinedBlockState.runtimeBlocks,
      [ComputeContextKeys.INTERFACE_RECORD]: getInterfaceRecordForState(
        interfaceRecord,
        this.state.activeAppVariantKey,
      ),
      [ComputeContextKeys.PAGE_INPUTS]: this.state.pageInputs,
      [ComputeContextKeys.PAGE_PARAMS]: this.state.pageParams,
      [ComputeContextKeys.LOCATION]: this.state.location,
      // user fields plus the derived legacy `context` view of the device slice, so
      // pre-existing `userContext.context.*` bindings keep resolving.
      [ComputeContextKeys.USER_CONTEXT]: getUserContextWithLegacyDeviceContext(
        this.state.userContext,
        this.state.deviceContext,
      ),
      [ComputeContextKeys.DEVICE]: this.state.deviceContext,
      [ComputeContextKeys.APPS_AND_INTEGRATIONS]: this.state.appsAndIntegrations,
      [ComputeContextKeys.THEME]: {
        colorScheme: this.state.colorScheme,
        activeTheme: this.state.activeTheme,
        availableThemes: this.state.availableThemes,
      },
      [ComputeContextKeys.PAGE_FUNCTIONS]: combinedPageFunctions,
      [ComputeContextKeys.PAGE_VARIABLES]: adaptedPageVariables,
      [ComputeContextKeys.SESSION_STORAGE]: {
        ...this.methods.sessionStorageMethods,
        ...this.state.sessionStorage.values,
      },
      [ComputeContextKeys.DEPRECATED_SESSION_STORAGE]: {
        data: this.state.sessionStorage.values,
      },
      [ComputeContextKeys.LOCAL_STORAGE]: {
        ...this.state.localStorage.values,
        ...this.methods.localStorageMethods,
        // for backward compatibility
        values: this.state.localStorage.values,
      },
      [ComputeContextKeys.UTILS]:
        InterfaceHelper.getInterfacePlatform(interfaceRecord) === ApplicationType.MOBILE
          ? getMobileContextUtils(this.state.queryMeta)
          : getContextUtils(this.state.queryMeta),
      [ComputeContextKeys.ENV_VARIABLES]: this.state.envVariables,
      [ComputeContextKeys.QUERY_META]: this.state.queryMeta,
      [ComputeContextKeys.MEDIA_UPLOADS]: mediaUploads,
      // In readonly (the only shareable shape) actions is always empty, so baking it
      // into the base leaks nothing across siblings; mutable builders never share, so
      // baking in their own actions is fine too.
      [ComputeContextKeys.ACTIONS]: this.methods.actions,
    };

    const baseContext = {
      ...result,
      ...combinedBlockState,
      ...combinedDataSourceState,
      ...combinedPageFunctions,
      ...combinedPageVariables,
    };

    this.recordLastBase(baseContext);

    return baseContext;
  }

  /**
   * Applies the inside-Repeatable layer on top of baseContext:
   * - row-scope identity rewrites (so bare `b_outerInput.value` resolves from
   *   inside a row via the inner-first stack walk)
   * - runtime block materialization (combining instance states from
   *   blocksRuntimeState)
   *
   * The instance-attachment pass runs again here even though base already paid
   * for it — attachInstances has no top-level memo, and the row-scope rewrite
   * must layer on top of an instance-attached map for cross-row reach from
   * inside a row to resolve. Net cost matches the pre-split implementation.
   */
  private applyRuntimeBlocksContext(
    baseContext: ComputeContextType,
    blocksRuntimeState: Record<string, Record<string, unknown>>,
  ): ComputeContextType {
    const blocksStateWithMethod = this.getCachedCombinedBlocksWithMethods(
      this.state.blockStates,
      this.methods.blockMethods,
    );
    const withInstances = this.attachInstancesToBlocksState(blocksStateWithMethod);
    const withRowScope = this.attachRowScopeToBlocksState(withInstances);

    const combinedBlockState = getCombinedBlockState({
      blocksState: withRowScope,
      blocksRuntimeState,
    });

    return {
      ...baseContext,
      [ComputeContextKeys.RUNTIME_BLOCKS]: combinedBlockState.runtimeBlocks,
      ...combinedBlockState,
    };
  }

  /**
   * Returns the base context (without blocksRuntimeState), reusing in order:
   *   1. this builder's own last base (cheapest — no map lookup),
   *   2. the process-wide readonly base already built by a sibling builder,
   *   3. a fresh build.
   * `reusedOwn` is true only for (1), so the caller knows its cached
   * runtime-overlay result is still valid.
   */
  private getOrBuildBaseContext(): { base: ComputeContextType; reusedOwn: boolean } {
    if (this.canReuseBaseContext() && this.lastBaseContext) {
      return { base: this.lastBaseContext, reusedOwn: true };
    }

    // Only the readonly shape can be shared (see isShareableReadonly).
    const isReadonlyShareable = this.isShareableReadonly();
    if (isReadonlyShareable) {
      const shared = sharedReadonlyBaseCache.get(this.state.blockStates);
      if (
        shared &&
        this.stateMatchesSnapshot(
          shared.inputs,
          shared.methods,
          shared.runtimeSourceInfo,
          undefined,
          shared.runtimeSourceBlockIds,
          true,
        )
      ) {
        // Adopt the sibling's base as our own last base so future builds on THIS
        // builder take the cheap per-instance path above.
        this.recordLastBase(shared.base);
        return { base: shared.base, reusedOwn: false };
      }
    }
    const base = this.buildBaseContext();
    if (isReadonlyShareable) {
      sharedReadonlyBaseCache.set(this.state.blockStates, {
        inputs: this.lastBuildInputs as ContextState,
        methods: this.lastBuildMethods as ContextMethodsState,
        runtimeSourceInfo: this.lastBuildRuntimeSourceInfo,
        runtimeSourceBlockIds: this.lastBuildRuntimeSourceBlockIds,
        base,
      });
    }
    return { base, reusedOwn: false };
  }

  // The pure-readonly shape both process-wide caches require: no per-instance action
  // closures (repeatableActions) and no attached entity methods. This is the ONE
  // definition of "shareable" — the base cache and the row-overlay cache both gate on
  // it, so a builder-specific closure can never leak across builders. `actions` counts
  // as a method here: it is produced only in mutable mode, so a builder that carries
  // actions is mutable and must not touch either shared cache.
  private isShareableReadonly(): boolean {
    if (this.repeatableActions !== undefined) return false;
    for (const methodKey of ContextBuilder.METHODS_KEYS) {
      if (Object.keys(this.methods[methodKey]).length > 0) return false;
    }
    return true;
  }

  // Single writer for the per-instance "last base" bookkeeping. Both a fresh build
  // (buildBaseContext) and adopting a sibling's shared base (getOrBuildBaseContext)
  // route through here, so the fields canReuseBaseContext reads stay in lockstep
  // and an adopted base is indistinguishable from a self-built one.
  private recordLastBase(base: ComputeContextType): void {
    // Store the input refs directly, not copies: state/methods are only ever
    // replaced wholesale (setState/setMethods build a new object), never mutated
    // in place, so the captured ref is a faithful snapshot of the build inputs.
    this.lastBuildInputs = this.state;
    this.lastBuildMethods = this.methods;
    this.lastBaseContext = base;
    this.lastBuildRuntimeSourceInfo = this.runtimeSourceInfo;
    this.lastBuildRepeatableActions = this.repeatableActions;
    this.lastBuildRuntimeSourceBlockIds = this.runtimeSourceBlockIds;
  }

  /**
   * Public method to build the context, with caching
   * Strategy:
   * 1. Build/reuse base context (without blocksRuntimeState) - works for 99% of blocks
   * 2. If blocksRuntimeState is present, merge it with the base context
   */
  build(): ComputeContextType {
    const { base: baseContext, reusedOwn } = this.getOrBuildBaseContext();

    // if no runtime state, the base IS the result.
    if (!this.blocksRuntimeState) {
      this.lastBuildResult = baseContext;
      this.lastBuildBlocksRuntimeState = undefined;
      return baseContext;
    }

    // Reuse the merged result only when the base is unchanged (our own last base)
    // AND the runtime state is unchanged — otherwise the cached result is stale.
    if (
      reusedOwn &&
      this.lastBuildResult &&
      isEqual(this.blocksRuntimeState, this.lastBuildBlocksRuntimeState)
    ) {
      return this.lastBuildResult;
    }

    // Cross-sibling row-overlay sharing (isShareableReadonly, keyed on the per-row
    // blocksRuntimeState ref so different rows never collide): siblings in one row
    // build an identical row-scoped overlay, so the first builds it and the rest adopt.
    const isRowShareable =
      this.isShareableReadonly() &&
      this.rowScopeStack !== undefined &&
      this.rowScopeStack.length > 0;

    let result: ComputeContextType | undefined;
    if (isRowShareable) {
      const shared = sharedRowOverlayCache.get(this.blocksRuntimeState);
      // base/blockStates/rowScopeStack are ref-shared across a row's siblings; the
      // per-builder RuntimeSourceInfo wrapper differs by ref so compare its fields.
      if (
        shared &&
        shared.base === baseContext &&
        shared.blockStates === this.state.blockStates &&
        shared.rowScopeStack === this.rowScopeStack &&
        isRuntimeSourceInfoContentEqual(this.runtimeSourceInfo, shared.runtimeSourceInfo)
      ) {
        result = shared.result;
      }
    }

    if (result === undefined) {
      result = this.applyRuntimeBlocksContext(baseContext, this.blocksRuntimeState);
      if (isRowShareable) {
        sharedRowOverlayCache.set(this.blocksRuntimeState, {
          base: baseContext,
          blockStates: this.state.blockStates,
          rowScopeStack: this.rowScopeStack,
          runtimeSourceInfo: this.runtimeSourceInfo,
          result,
        });
      }
    }

    this.lastBuildResult = result;
    this.lastBuildBlocksRuntimeState = this.blocksRuntimeState;

    return result;
  }

  /**
   * Updates methods with new values, maintaining immutability
   */
  setMethods(newMethods: Partial<ContextMethodsState>): this {
    this.methods = { ...this.methods, ...newMethods };
    return this;
  }

  setBlockStates(blockStates: ContextState['blockStates']): this {
    return this.setState({ blockStates });
  }

  setBlockRefs(blockRefs: ContextState['blockRefs']): this {
    return this.setState({ blockRefs });
  }

  setWalkthrough(walkthrough: ContextState['walkthrough']): this {
    return this.setState({ walkthrough });
  }

  setDataSources(dataSources: ContextState['dataSources']): this {
    return this.setState({ dataSources });
  }

  setDataSourcesMeta(dataSourcesMeta: ContextState['dataSourcesMeta']): this {
    return this.setState({ dataSourcesMeta });
  }

  setPageInputs(pageInputs: ContextState['pageInputs']): this {
    return this.setState({ pageInputs });
  }

  setPageParams(pageParams: ContextState['pageParams']): this {
    return this.setState({ pageParams });
  }

  setLocation(location: ContextState['location']): this {
    return this.setState({ location });
  }

  setUserContext(userContext: ContextState['userContext']): this {
    return this.setState({ userContext });
  }

  setDeviceContext(deviceContext: ContextState['deviceContext']): this {
    return this.setState({ deviceContext });
  }

  setAppsAndIntegrations(appsAndIntegrations: ContextState['appsAndIntegrations']): this {
    return this.setState({ appsAndIntegrations });
  }

  setPageVariables(pageVariables: ContextState['pageVariables']): this {
    return this.setState({ pageVariables });
  }

  setPageFunctions(pageFunctions: ContextState['pageFunctions']): this {
    return this.setState({ pageFunctions });
  }

  setSessionStorage(sessionStorage: ContextState['sessionStorage']): this {
    return this.setState({ sessionStorage });
  }

  setLocalStorage(localStorage: ContextState['localStorage']): this {
    return this.setState({ localStorage });
  }

  setInterfaceRecord(interfaceRecord: ContextState['interfaceRecord']): this {
    return this.setState({ interfaceRecord });
  }

  setBlocksRuntimeState(blocksRuntimeState?: Record<string, Record<string, unknown>>): this {
    this.blocksRuntimeState = blocksRuntimeState;
    return this;
  }

  setRuntimeSourceInfo(runtimeSourceInfo: RuntimeSourceInfo | undefined): this {
    this.runtimeSourceInfo = runtimeSourceInfo;
    return this;
  }

  setRowScopeStack(rowScopeStack: readonly RowScope[] | undefined): this {
    this.rowScopeStack = rowScopeStack;
    return this;
  }

  setRepeatableActions(repeatableActions: RepeatableActions | undefined): this {
    this.repeatableActions = repeatableActions;
    return this;
  }

  setRuntimeSourceBlockIds(runtimeSourceBlockIds: ReadonlySet<string> | undefined): this {
    this.runtimeSourceBlockIds = runtimeSourceBlockIds;
    return this;
  }

  setEnvVariables(envVariables: ContextState['envVariables']): this {
    return this.setState({ envVariables });
  }

  setQueryMeta(queryMeta: ContextState['queryMeta']): this {
    return this.setState({ queryMeta });
  }

  setMediaUploads(mediaUploads: ContextState['mediaUploads']): this {
    return this.setState({ mediaUploads });
  }

  setColorScheme(colorScheme: NonNullable<ContextState['colorScheme']>): this {
    return this.setState({ colorScheme });
  }

  setActiveAppVariantKey(activeAppVariantKey: ContextState['activeAppVariantKey']): this {
    return this.setState({ activeAppVariantKey });
  }

  setActiveTheme(activeTheme: ContextState['activeTheme']): this {
    return this.setState({ activeTheme });
  }

  setAvailableThemes(availableThemes: ContextState['availableThemes']): this {
    return this.setState({ availableThemes });
  }
}

export default ContextBuilder;
